// Görev atama — a manager/şef hands a task to someone in their own reach.
//
// WHY THIS EXISTS: POST /v1/tasks has shipped since PHASE_4 with a real
// reach check (canAssignTaskTo) and NO caller in the product. Every task in
// the demo inbox was written by a seed or by another module's side effect, so
// "assign work to your team" — the thing a manager opens a task list to do —
// could only be demonstrated with curl.
//
// The assignee list is the directory's first page filtered to the caller's
// reach ON THE SERVER: GET /v1/employees is already tier-scoped, so a
// manager_subtree caller sees their subtree and nothing else. We do NOT
// re-implement that predicate here — a client-side copy of a permission rule is
// the drift-shut defect class this repo has already paid for. The 403 path
// stays reachable and honest: the server is the authority, and if it refuses
// the refusal is rendered rather than hidden.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { fetchEmployeesFirstPage } from "@/features/employees/api";
import { createTask, type TaskRow } from "./api";

/**
 * `kind` is a free-text column on the route; rows created from this screen are
 * tagged so they are distinguishable from the ones other modules emit (an
 * onboarding fan-out, an approval nudge). One literal, one meaning.
 */
const MANUAL_TASK_KIND = "manual_assignment";

const PRIORITIES = ["low", "normal", "high", "urgent"] as const;

export interface AssignTaskModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onAssigned: () => void;
}

export function AssignTaskModal({
  open,
  onClose,
  onAssigned,
}: AssignTaskModalProps): React.ReactElement {
  const { t } = useTranslation();
  const [assignee, setAssignee] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<(typeof PRIORITIES)[number]>("normal");
  const [dueAt, setDueAt] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Only fetched while the modal is open: the directory is a heavier list than
  // the task page needs, and a closed modal must not pay for it on every render
  // of the screen behind it.
  const employeesQuery = useQuery({
    queryKey: ["employees", "assign-task-picker"],
    queryFn: fetchEmployeesFirstPage,
    enabled: open,
  });

  async function onSubmit(): Promise<void> {
    setSubmitError(null);
    if (assignee === "" || title.trim() === "") {
      setSubmitError(t("tasks.assign_error_required"));
      return;
    }
    setSubmitting(true);
    try {
      await createTask({
        assignee_employee_id: assignee,
        kind: MANUAL_TASK_KIND,
        title: title.trim(),
        ...(description.trim() !== "" && { description: description.trim() }),
        priority,
        // datetime-local yields local wall-clock; the route wants an ISO
        // instant, so the conversion happens here rather than in the schema.
        ...(dueAt !== "" && { due_at: new Date(dueAt).toISOString() }),
      });
      onAssigned();
      onClose();
      setAssignee("");
      setTitle("");
      setDescription("");
      setDueAt("");
    } catch (err) {
      setSubmitError(errorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title={t("tasks.assign_title")} />
      <ModalBody>
        <p className="mb-3 text-sm text-[var(--color-muted-foreground)]">
          {t("tasks.assign_subtitle")}
        </p>
        {submitError !== null && (
          <p role="alert" className="mb-3 text-sm text-[var(--color-error)]">
            {submitError}
          </p>
        )}
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-muted-foreground)]">
              {t("tasks.assign_assignee_label")}
            </span>
            <select
              aria-label={t("tasks.assign_assignee_label")}
              className="rounded border px-3 py-2 text-sm"
              value={assignee}
              onChange={(e): void => setAssignee(e.target.value)}
            >
              <option value="">{t("tasks.assign_assignee_placeholder")}</option>
              {(employeesQuery.data?.items ?? []).map((e) => (
                <option key={e.employee_id} value={e.employee_id}>
                  {`${e.legal_first_name} ${e.legal_last_name}`}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-muted-foreground)]">
              {t("tasks.assign_title_label")}
            </span>
            <input
              type="text"
              maxLength={500}
              aria-label={t("tasks.assign_title_label")}
              className="rounded border px-3 py-2 text-sm"
              value={title}
              onChange={(e): void => setTitle(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-muted-foreground)]">
              {t("tasks.assign_description_label")}
            </span>
            <textarea
              rows={3}
              maxLength={5000}
              aria-label={t("tasks.assign_description_label")}
              className="rounded border px-3 py-2 text-sm"
              value={description}
              onChange={(e): void => setDescription(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-muted-foreground)]">
              {t("tasks.assign_priority_label")}
            </span>
            <select
              aria-label={t("tasks.assign_priority_label")}
              className="rounded border px-3 py-2 text-sm"
              value={priority}
              onChange={(e): void => setPriority(e.target.value as (typeof PRIORITIES)[number])}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`tasks.priority.${p}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-muted-foreground)]">
              {t("tasks.assign_due_label")}
            </span>
            <input
              type="datetime-local"
              aria-label={t("tasks.assign_due_label")}
              className="rounded border px-3 py-2 text-sm"
              value={dueAt}
              onChange={(e): void => setDueAt(e.target.value)}
            />
          </label>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" size="sm" onClick={onClose}>
          {t("tasks.assign_cancel")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={(): void => void onSubmit()}
          disabled={submitting}
        >
          {t("tasks.assign_submit")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export type { TaskRow };
